/**
 * 用户信息相关工具函数
 */

export const ADMIN_ROLE = "Admin";
export const SYSTEM_LOG_ROLES = [ADMIN_ROLE, "Auditor", "Manager"] as const;
export const USER_INFO_STORAGE_KEY = "user_info";
export const AUTH_SESSION_CHANGED_EVENT = "azopsmate-auth-session-changed";
export const AUTH_REFRESH_REQUESTED_EVENT = "azopsmate-auth-refresh-requested";
export const USER_ROLE_DISPLAY_PRIORITY = [
  ADMIN_ROLE,
  "Manager",
  "Auditor",
  "CSM",
  "CRA",
  "Staff",
] as const;

type UserRoleValue =
  | string
  | {
      RoleCode?: unknown;
      roleCode?: unknown;
      RoleName?: unknown;
      roleName?: unknown;
    };

export function extractUserRoles(roles: unknown): string[] {
  if (!Array.isArray(roles)) return [];

  const values = roles.flatMap((role: UserRoleValue) => {
    if (typeof role === "string") return [role];
    if (!role || typeof role !== "object") return [];
    return [role.RoleCode, role.roleCode, role.RoleName, role.roleName].filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
  });

  return values.filter(
    (role, index) =>
      values.findIndex((candidate) => candidate.toLowerCase() === role.toLowerCase()) === index,
  );
}

/**
 * 获取账号区域要显示的最高优先级角色。
 * 兼容 checkuserinfo 返回的角色对象以及仅包含角色名的字符串数组。
 */
export function getHighestPriorityUserRole(roles: unknown): string {
  const roleValues = extractUserRoles(roles);

  const normalizedRoles = new Set(roleValues.map((role) => role.toLowerCase()));
  const highestPriorityRole = USER_ROLE_DISPLAY_PRIORITY.find((role) =>
    normalizedRoles.has(role.toLowerCase()),
  );

  return highestPriorityRole ?? roleValues[0] ?? "";
}

/**
 * 从 localStorage 获取当前用户信息
 */
export function getCurrentUser(): Record<string, any> | null {
  try {
    const raw = localStorage.getItem(USER_INFO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function storeCurrentUser(user: Record<string, unknown>): void {
  localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(user));
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

export function clearCurrentUser(): void {
  localStorage.removeItem(USER_INFO_STORAGE_KEY);
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

/**
 * 获取当前用户全部角色编码
 */
export function getCurrentUserRoles(): string[] {
  const user = getCurrentUser();
  return extractUserRoles(user?.Roles ?? user?.Role ?? user?.roles);
}

export function userHasAnyRole(roles: unknown, allowedRoles: readonly string[]): boolean {
  const normalizedAllowedRoles = new Set(allowedRoles.map((role) => role.toLowerCase()));
  return extractUserRoles(roles).some((role) => normalizedAllowedRoles.has(role.toLowerCase()));
}

/**
 * 判断当前用户是否具有任一指定角色
 */
export function currentUserHasAnyRole(allowedRoles: readonly string[]): boolean {
  return userHasAnyRole(getCurrentUserRoles(), allowedRoles);
}

/**
 * 获取当前用户 ID
 */
export function getCurrentUserId(): string | undefined {
  const user = getCurrentUser();
  if (!user) return undefined;
  const id = user.UserId ?? user.userId ?? user.ID;
  return id ? String(id) : undefined;
}

/**
 * 判断当前用户是否具有 Admin 角色
 */
export function isCurrentUserAdmin(): boolean {
  return currentUserHasAnyRole([ADMIN_ROLE]);
}

/**
 * 判断当前用户是否可访问 Admin - System Logs 菜单
 */
export function canCurrentUserAccessSystemLogs(): boolean {
  return currentUserHasAnyRole(SYSTEM_LOG_ROLES);
}

/**
 * 当前用户进入后台时的默认落点
 */
export function getCurrentUserAdminDefaultPath(): string {
  return getAdminDefaultPathForRoles(getCurrentUserRoles());
}

export function getAdminDefaultPathForRoles(roles: unknown): string {
  if (userHasAnyRole(roles, [ADMIN_ROLE])) return "/admin/analytics";
  if (userHasAnyRole(roles, SYSTEM_LOG_ROLES)) return "/admin/logs/update-logs";
  return "/";
}
